import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    IconButton,
    Typography,
    Paper,
    CircularProgress,
    Grid,
} from "@mui/material";
import { Delete, CloudUpload, ArrowUpward, ArrowDownward } from "@mui/icons-material";
import { toast } from "react-toastify";
import apiClient from "../utils/apiClient";
import FallbackImage from "../components/FallbackImage";
import { getImageFullUrl } from "../utils/common";

const SliderManagement = () => {
    const [sliders, setSliders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Fetch all sliders
    const fetchSliders = async () => {
        try {
            setLoading(true);
            const res = await apiClient.get("/api/sliders");
            setSliders(res.data.data || []);
        } catch (err) {
            toast.error("Failed to fetch sliders");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSliders();
    }, []);

    // Upload new slider
    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Only JPG, PNG, or WEBP images are allowed!");
            return;
        }


        const formData = new FormData();
        formData.append("image", file);

        try {
            setUploading(true);
            await apiClient.post("/api/sliders", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Slider uploaded");
            fetchSliders();
        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    // Delete slider
    const handleDelete = async (id) => {
        try {
            await apiClient.delete(`/api/sliders/${id}`);
            toast.success("Slider deleted");
            fetchSliders();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    // Update slider position
    const handlePositionChange = async (id, newPosition) => {
        try {
            await apiClient.put(`/api/sliders/${id}/position`, { position: newPosition });
            fetchSliders();
        } catch (err) {
            toast.error("Failed to update position");
        }
    };

    return (
        <Box>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Home Page Slider Management
                </Typography>

                {/* Upload Button */}
                <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUpload />}
                    sx={{ mb: 3 }}
                    disabled={uploading}
                >
                    {uploading ? "Uploading..." : "Upload New Slider"}
                    <input hidden accept="image/*" type="file" onChange={handleUpload} />
                </Button>

                {/* Sliders List */}
                {loading ? (
                    <Box textAlign="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : sliders.length === 0 ? (
                    <Typography>No sliders found</Typography>
                ) : (
                    <Grid container spacing={3}>
                        {sliders.map((slider, index) => (
                            <Grid item size={{xs:12, sm:6, md:4}}  key={slider.sliderId}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        textAlign: "center",
                                        boxShadow: 3,
                                    }}
                                >
                                    {/* Slider Image */}
                                    <FallbackImage
                                        src={getImageFullUrl(slider.image)}
                                        alt="Slider"
                                        style={{
                                            width: "100%",
                                            height: "180px",
                                            objectFit: "cover",
                                            borderRadius: "12px",
                                        }}
                                    />

                                    {/* Controls */}
                                    {/* <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            mt: 2,
                                            gap: 1,
                                        }}
                                    >
                                        <IconButton
                                            onClick={() =>
                                                handlePositionChange(slider.sliderId, slider.position - 1)
                                            }
                                            disabled={index === 0}
                                            color="primary"
                                        >
                                            <ArrowUpward />
                                        </IconButton>
                                        <Typography variant="body2" fontWeight="bold">
                                            Position: {slider.position}
                                        </Typography>
                                        <IconButton
                                            onClick={() =>
                                                handlePositionChange(slider.sliderId, slider.position + 1)
                                            }
                                            disabled={index === sliders.length - 1}
                                            color="primary"
                                        >
                                            <ArrowDownward />
                                        </IconButton>
                                    </Box> */}

                                    {/* Delete */}
                                    <IconButton
                                        onClick={() => handleDelete(slider.sliderId)}
                                        color="error"
                                        sx={{ mt: 1 }}
                                    >
                                        <Delete />
                                    </IconButton>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Paper>
        </Box>
    );
};

export default SliderManagement;
